// RiskMonitor — AI-computed member risk score panel.
// Shows aggregate risk counts and a table of high/critical members.
// Admin-only: only renders when user.role === 'admin'.
// Collapsible/expandable.

import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useRiskSummary, useRiskScores } from "../../hooks/useAI";
import type { RiskScoreRow } from "../../hooks/useAI";
import { useMembers, MEMBERS_PAGE_LIMIT } from "../../hooks/useDashboard";
import type { MemberRow } from "../../hooks/useDashboard";
import TruncationNotice from "../shared/TruncationNotice";

// ── Helpers ──────────────────────────────────────────────────────────────────

function riskLevelColor(level: string): string {
  switch (level) {
    case "critical":
      return "text-red-700 dark:text-red-400";
    case "high":
      return "text-orange-600 dark:text-orange-400";
    case "moderate":
      return "text-yellow-600 dark:text-yellow-400";
    default:
      return "text-green-600 dark:text-green-400";
  }
}

function riskBadgeBg(level: string): string {
  switch (level) {
    case "critical":
      return "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300";
    case "high":
      return "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300";
    case "moderate":
      return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300";
    default:
      return "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300";
  }
}

function scoreBarColor(score: number): string {
  if (score >= 80) return "bg-red-500";
  if (score >= 60) return "bg-orange-500";
  if (score >= 30) return "bg-yellow-500";
  return "bg-green-500";
}

// ── Summary badges ────────────────────────────────────────────────────────────

interface BadgeProps {
  label: string;
  count: number;
  bgClass: string;
}

function RiskBadge({ label, count, bgClass }: BadgeProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-lg px-4 py-2.5 min-w-[72px] ${bgClass}`}
    >
      <span className="text-2xl font-bold tabular-nums leading-none">
        {count}
      </span>
      <span className="text-xs font-medium mt-0.5 capitalize">{label}</span>
    </div>
  );
}

// ── Member row ────────────────────────────────────────────────────────────────

function buildMemberIndex(members: MemberRow[]): Map<string, MemberRow> {
  const map = new Map<string, MemberRow>();
  for (const m of members) {
    // The API returns rows with normalised snake_case keys
    const rawId =
      (m as Record<string, unknown>)["airtable_id"] ??
      (m as Record<string, unknown>)["id"];
    if (typeof rawId === "string" && rawId) map.set(rawId, m);
  }
  return map;
}

function MemberRiskRow({
  score,
  member,
}: {
  score: RiskScoreRow;
  member: MemberRow | undefined;
}) {
  const name =
    (member as Record<string, unknown> | undefined)?.["full_name"] ??
    (member as Record<string, unknown> | undefined)?.["Full_Name"] ??
    `Member #${score.member_id}`;
  const [expanded, setExpanded] = useState(false);
  const signals = score.signals ?? [];
  const hasBreakdown = signals.length > 0;

  return (
    <>
      <tr
        className="border-b last:border-0 cursor-pointer hover:bg-theme-overlay/30"
        style={{ borderColor: "var(--border-subtle)" }}
        onClick={() => hasBreakdown && setExpanded((v) => !v)}
        title={hasBreakdown ? "Click for signal breakdown" : ""}
      >
        <td
          className="py-2.5 px-3 text-sm font-medium"
          style={{ color: "var(--text-primary)" }}
        >
          {String(name)}
        </td>
        <td className="py-2.5 px-3">
          <div className="flex items-center gap-2">
            <div
              className="w-20 rounded-full h-2 overflow-hidden"
              style={{ background: "var(--surface-subtle)" }}
            >
              <div
                className={`h-2 rounded-full transition-all duration-300 ${scoreBarColor(score.score)}`}
                style={{ width: `${score.score}%` }}
              />
            </div>
            <span
              className={`text-xs font-semibold tabular-nums ${riskLevelColor(score.risk_level)}`}
            >
              {Math.round(score.score)}
            </span>
          </div>
        </td>
        <td className="py-2.5 px-3">
          <span
            className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${riskBadgeBg(score.risk_level)}`}
          >
            {score.risk_level}
          </span>
        </td>
        <td className="py-2.5 px-3">
          <div className="flex flex-wrap gap-1">
            {(score.top_signals ?? []).map((sig, i) => (
              <span
                key={i}
                className="text-xs px-1.5 py-0.5 rounded"
                style={{
                  background: "var(--surface-subtle)",
                  color: "var(--text-secondary)",
                }}
              >
                {sig}
              </span>
            ))}
            {hasBreakdown && (
              <span className="text-xs text-theme-muted">
                {expanded ? "▴" : "▾"}
              </span>
            )}
          </div>
        </td>
      </tr>
      {expanded && hasBreakdown && (
        <tr style={{ background: "var(--surface-overlay)" }}>
          <td colSpan={4} className="px-3 py-3">
            <div className="flex flex-col gap-1.5 max-w-2xl">
              {signals
                .slice()
                .sort((a, b) => b.weight - a.weight)
                .map((s, i) => {
                  const pct = Math.min(
                    100,
                    Math.round((s.weight / Math.max(1, score.score)) * 100),
                  );
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="min-w-[200px] text-theme-secondary">
                        {s.name}
                      </span>
                      <div
                        className="flex-1 rounded-full h-1.5 overflow-hidden"
                        style={{ background: "var(--surface-subtle)" }}
                      >
                        <div
                          className="h-full rounded-full bg-red-500/70"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="font-mono tabular-nums w-8 text-right text-theme-muted">
                        +{s.weight}
                      </span>
                    </div>
                  );
                })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function RiskMonitor() {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const { data: summaryData, isLoading: summaryLoading } = useRiskSummary();
  const { data: scoresData, isLoading: scoresLoading } = useRiskScores();
  // Limit 5,000 = the API hard maximum. Live: 1,895 rows in dash_members
  // (2026-08-27), 2.6x headroom. This asked for 1,000 until 2026-08-27, so the
  // member lookup this panel joins risk scores against was missing 895 people —
  // a scored member past rowid 1,000 rendered with no name.
  const { data: membersData } = useMembers({ limit: MEMBERS_PAGE_LIMIT });

  // Only show to admin users
  if (!user || user.role !== "admin") return null;

  const summary = summaryData ?? {
    critical: 0,
    high: 0,
    moderate: 0,
    low: 0,
    total: 0,
    computedAt: null,
  };
  const allScores = scoresData?.scores ?? [];

  // Table shows only high + critical
  const elevatedScores = allScores.filter(
    (s) => s.risk_level === "critical" || s.risk_level === "high",
  );

  const memberIndex = buildMemberIndex(membersData?.members ?? []);

  // The table shows high+critical scored members
  // score.member_id is the airtable_id — join with memberIndex

  const computedLabel = summary.computedAt
    ? `Last computed ${new Date(summary.computedAt + "Z").toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
    : "Not yet computed — trigger nightly run to populate";

  const hasData = summary.total > 0;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-theme-overlay/40 transition-colors"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3">
          {/* Shield icon */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "var(--text-muted)" }}
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <div className="text-left">
            <p
              className="text-sm font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              AI Risk Monitor
            </p>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {computedLabel}
            </p>
          </div>
          {summary.critical > 0 && (
            <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
              {summary.critical} critical
            </span>
          )}
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`}
          style={{ color: "var(--text-muted)" }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Expandable content */}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
          {/* Member-lookup completeness. The table below joins each risk score
              to a member row by airtable_id; a member the lookup did not load
              renders as a bare "Member #recXXXX" id instead of a name. Until
              2026-08-27 this panel asked for 1,000 of dash_members' 1,895 rows,
              so 895 people could not be named and nothing on the wire said so.
              The notice renders nothing when the lookup proves it is whole, so
              no wrapper here — an always-present padded div would leave a gap
              on the healthy path. */}
          <TruncationNotice
            response={membersData}
            label="member lookup"
            hint="A risk row whose member did not load shows a bare record id instead of a name."
            className="mx-5 mt-4"
          />

          {/* Risk badge summary */}
          <div className="px-5 py-4 flex flex-wrap gap-3">
            {summaryLoading && !hasData ? (
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                Loading risk data…
              </p>
            ) : !hasData ? (
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                No risk scores yet. Trigger the nightly intelligence run via{" "}
                <code
                  className="font-mono text-xs px-1 rounded"
                  style={{ background: "var(--surface-subtle)" }}
                >
                  POST /api/ai/trigger-nightly
                </code>{" "}
                to populate this panel.
              </p>
            ) : (
              <>
                <RiskBadge
                  label="Critical"
                  count={summary.critical}
                  bgClass="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                />
                <RiskBadge
                  label="High"
                  count={summary.high}
                  bgClass="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300"
                />
                <RiskBadge
                  label="Moderate"
                  count={summary.moderate}
                  bgClass="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
                />
                <RiskBadge
                  label="Low"
                  count={summary.low}
                  bgClass="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300"
                />
                <div className="ml-auto flex items-center">
                  <span
                    className="text-xs"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {summary.total} active members scored
                  </span>
                </div>
              </>
            )}
          </div>

          {/* High + Critical member table */}
          {hasData && elevatedScores.length > 0 && (
            <div className="px-5 pb-5">
              <p
                className="text-xs font-semibold mb-2 uppercase tracking-wide"
                style={{ color: "var(--text-muted)" }}
              >
                High &amp; Critical Risk Members ({elevatedScores.length})
              </p>
              <div
                className="rounded-lg overflow-hidden"
                style={{ border: "1px solid var(--border-subtle)" }}
              >
                <table className="w-full text-left">
                  <thead>
                    <tr style={{ background: "var(--surface-subtle)" }}>
                      <th
                        className="py-2 px-3 text-xs font-semibold uppercase tracking-wide"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Member
                      </th>
                      <th
                        className="py-2 px-3 text-xs font-semibold uppercase tracking-wide"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Score
                      </th>
                      <th
                        className="py-2 px-3 text-xs font-semibold uppercase tracking-wide"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Level
                      </th>
                      <th
                        className="py-2 px-3 text-xs font-semibold uppercase tracking-wide"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Signals
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{ background: "var(--surface-raised)" }}>
                    {scoresLoading && elevatedScores.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="py-4 px-3 text-sm text-center"
                          style={{ color: "var(--text-muted)" }}
                        >
                          Loading…
                        </td>
                      </tr>
                    ) : (
                      elevatedScores.map((s) => (
                        <MemberRiskRow
                          key={s.member_id}
                          score={s}
                          member={memberIndex.get(String(s.member_id))}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {hasData && elevatedScores.length === 0 && !scoresLoading && (
            <div className="px-5 pb-5">
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                No high or critical risk members. All {summary.total} active
                members are at low or moderate risk.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
